import type { Media_Url_Fragment$data } from '@/graphql/mutations/Media.mutation';
import type { MediaItem, MediaItemSizeEnum } from '@/graphql/types';
import type { FragmentData } from '@/graphql/utils';
import { FastAverageColor } from 'fast-average-color';
import slufigy from 'react-slugify';
/**
 * For a given image - get the average color and return it as a rgba string
 */

const cache = new Map<string, string | null>();

export const getImageBackgroundColor = async (image: string | null | undefined): Promise<string | null> => {
  if (!image) return null;

  if (cache.has(image)) {
    return cache.get(image) ?? null;
  }
  const fac = new FastAverageColor();

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = image;

  try {
    const color = await fac.getColorAsync(img, { crossOrigin: 'anonymous' });
    const rgb = color.value;

    // create new rgba with opacity of 0.25
    const rgba = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.25)`;

    cache.set(image, rgba);
    return rgba;
  } catch (e) {
    cache.set(image, null);
    return null;
  }
};

export type ZnMediaItem = FragmentData<Media_Url_Fragment$data> | MediaItem;

export const getMediaItemUrl = (
  mediaItem: ZnMediaItem | null | undefined,
  preferredSize?: MediaItemSizeEnum,
  options: { imageOnly?: boolean } = {}
): string | null => {
  if (!mediaItem) return null;

  const defaultUrl = mediaItem?.mediaItemUrl ?? null;

  const tokens = mediaItem?.mediaItemUrl ? mediaItem?.mediaItemUrl?.split('.') : null;

  if (options?.imageOnly) {
    if (!mediaItem?.mimeType?.startsWith('image/')) {
      return null;
    }
  }

  if (!!tokens && tokens?.length > 0) {
    const ext = tokens[tokens.length - 1];
    if (['gif'].includes(ext)) return defaultUrl;
  }

  if (!preferredSize) return defaultUrl;

  const sizes = mediaItem?.mediaDetails?.sizes;
  if (!sizes?.length) return defaultUrl;

  const size = sizes.find((size) => size?.name?.toLowerCase() === preferredSize?.toLowerCase());
  if (!size) return defaultUrl;

  return size.sourceUrl ?? defaultUrl ?? null;
};

/**
 * Format the slug of a post
 */
export const getFormattedSlug = (
  value: string | null | undefined,
  opts?: { ensureUnique?: boolean }
): string | null => {
  if (!value) return null;
  let slug = value;

  // generate a unique slug by appending a random id to the slug
  if (opts?.ensureUnique) {
    const uuid = crypto.randomUUID();
    const tokens = uuid.split('-');

    // append the last uuid token
    const lastToken = tokens[tokens.length - 1];

    slug = `${slug}-${lastToken}`;
  }
  return slufigy(slug, { delimiter: '-' });
};

export function getMimeTypePlaceholder(mimeType: string): string {
  // Extract the general type (image, video, etc.)
  const type = mimeType.split('/')[0] || 'file';
  const subtype = mimeType.split('/')[1] || '';

  // Choose appropriate style based on mime type
  const style = (() => {
    switch (type) {
      case 'image':
        return 'icons';
      case 'video':
        return 'icons';
      case 'audio':
        return 'icons';
      case 'text':
        return 'identicon';
      case 'application':
        if (subtype.includes('pdf')) return 'icons';
        if (subtype.includes('zip') || subtype.includes('rar') || subtype.includes('tar')) return 'icons';
        if (subtype.includes('word') || subtype.includes('doc')) return 'icons';
        if (subtype.includes('excel') || subtype.includes('sheet')) return 'icons';
        if (subtype.includes('powerpoint') || subtype.includes('presentation')) return 'icons';
        return 'bottts';
      default:
        return 'bottts';
    }
  })();

  // Use file extension or subtype for seed to ensure consistent icons for same types
  const seed = encodeURIComponent(`file-${subtype || type}`);

  // Add customization based on mime type
  let options = '';
  if (style === 'bottts') {
    // Customize bottts style based on mime type
    const colors = encodeURIComponent(
      type === 'application' ? 'amber,cyan,green,indigo,red' : 'blue,cyan,green,purple'
    );
    options = `?seed=${seed}&backgroundColor=${colors}`;
  } else if (style === 'icons') {
    options = `?seed=${seed}`;
  } else {
    options = `?seed=${seed}`;
  }

  // Return the updated API URL
  return `https://api.dicebear.com/9.x/${style}/svg${options}`;
}
